import * as React from "react";
import { connect } from "react-redux";

import { Navigate } from "react-router";
import { Alert, Card, Select } from "@mantine/core";
import { IState } from "../../data/reducers/state";
import { IAssociation } from "../../types/association/Association";
import AvailabilityHistory from "../profile/AvailabilityHistory";
import { IProfileDashboardProps } from "../profile/ProfileDashboard";

export interface ICustomAvailabilityDashboardProps {
  association: IAssociation;
}

export interface ICustomAvailabilityDashboardState {
  selectedTitle: string | null;
}

export const CustomAvailabilityOptions: React.FunctionComponent<
  ICustomAvailabilityDashboardProps
> = (props) => {
  const [selectedTitle, setSelectedTitle] = React.useState<string | null>();

  const changeShift = (value: string | null) => {
    setSelectedTitle(value);
  };

  const customAvailabilities =
    props.association?.config?.availabilities?.custom;
  const link = customAvailabilities?.options?.filter(
    (op) => op.name == selectedTitle,
  )?.[0];

  return (
    <>
      <p style={{ color: "gray" }}>{customAvailabilities?.description}</p>
      <Select
        placeholder="Select a day of the week"
        value={selectedTitle || null}
        onChange={changeShift}
        data={(customAvailabilities?.options || []).map((li) => ({
          value: li.name,
          label: li.name,
        }))}
      />

      {link && (
        <>
          <hr />
          <p>{link.description}</p>
          <iframe
            title="Availabilities"
            id={`JotFormIFrame-${link.link}`}
            // onLoad={(e) => e.target.scrollTo(0, 0)}
            // allowTransparency={true}
            allowFullScreen
            allow="geolocation; microphone; camera"
            src={link.link}
            onLoadedData={(e) => console.log(e)}
            style={{
              minWidth: "100%",
              // height: "539px",
              border: "none",
              minHeight: "539px",
            }}
          />
        </>
      )}
    </>
  );
};

interface IAvailabilitiesDashboardProps extends IProfileDashboardProps {
  associations?: IAssociation[];
  personId: string;
}

const AvailabilitiesDashboardComponent: React.FunctionComponent<
  IAvailabilitiesDashboardProps
> = (props) => {
  if (!props.associations || props.associations.length == 0) {
    return (
      <p>
        <em>Loading...</em>
      </p>
    );
  }

  const usesAvailabilities = props.associations?.some(
    (a) => a?.config?.availabilities?.enabled,
  );

  if (!usesAvailabilities)
    return <p>Your association doesn't support availabilities at this time</p>;

  const associationsWithCustomAvailabilities = props.associations.filter(
    (a) => a.config.availabilities?.usesCustom,
  );
  const associationsWithInbuiltAvails = props.associations?.filter(
    (a) =>
      a?.config?.availabilities?.enabled &&
      a?.config?.availabilities?.usesInbuilt,
  );

  const defaultAvailabilityModes = new Set(
    associationsWithInbuiltAvails?.map(
      (a) => a?.config?.availabilities?.membersAreUnavailableByDefault || false,
    ),
  );

  let isUnavailableByDefault: boolean;
  const hasMultiAvailModes = defaultAvailabilityModes.size > 1;
  if (defaultAvailabilityModes.size === 0) {
    isUnavailableByDefault = false;
  } else {
    isUnavailableByDefault = defaultAvailabilityModes.has(true);
  }

  return (
    <>
      {associationsWithCustomAvailabilities.length > 0 &&
        associationsWithInbuiltAvails?.length > 0 && (
          <p>
            You have associations that use different availability forms, please
            ensure you submit your availability carefully
          </p>
        )}
      {associationsWithCustomAvailabilities.map((association) => (
        <Card
          key={`customavail-form-${association._id}`}
          padding="xl"
        >
          <h4>Custom availability form</h4>
          <p>
            <b>{association.name}</b> uses a custom availabilities form, this
            may be different to your other associations
          </p>

          <CustomAvailabilityOptions association={association} />
        </Card>
      ))}

      {associationsWithInbuiltAvails.length > 0 && (
        <Card padding="xl" mt="md">
          <p>
            <b>
              {associationsWithInbuiltAvails.map((a) => a?.name)?.join(", ")}
            </b>{" "}
            use this availabilities form:
          </p>
          {hasMultiAvailModes && (
            <Alert color="blue">
              <p>
                {associationsWithInbuiltAvails.map((a) => a?.name)?.join(", ")})
                use different availability modes (ie: permanent member of shifts
                AND you must use this view to indicate that you are available).
                By default, this view this view will use terminology to suggest
                you are unavailable by default, however you should take caution
                to correctly indicate your are available, or ensure you are not
                inadvertently available for specific shifts.
              </p>
            </Alert>
          )}
          <br />
          <AvailabilityHistory
            personId={props?.person?._id || undefined}
            isUnavailableByDefault={isUnavailableByDefault}
          />
        </Card>
      )}
    </>
  );
};

export const AvailabilitiesRedirect: React.FunctionComponent<{
  externalIdentifier: string;
}> = ({ externalIdentifier }) => {
  const link = `/u/${externalIdentifier || ""}/availabilities`;
  try {
    if (externalIdentifier) {
      return <Navigate to={link} />;
    }
    return <p>Loading availabilities...</p>;
  } catch (er) {
    return <Navigate to={link} />;
  }
};

function mapStateToAvailabilitiesDashboardProps(
  state: IState,
  props: IAvailabilitiesDashboardProps,
) {
  const personId = props?.person?._id || state.persons.self;

  return {
    personId,
    associations:
      state.persons?.map[personId]?.associations?.map(
        (a) => state.associations?.map?.[a.associationId],
      ) || [],
  };
}

export const AvailabilitiesDashboard = connect(
  mapStateToAvailabilitiesDashboardProps,
)(AvailabilitiesDashboardComponent);
